import {
	AlertTriangle,
	CheckCircle2,
	FolderTree,
	Gauge,
	Info,
	Loader2,
	Play,
	RotateCcw,
	Save,
	Settings as SettingsIcon,
	XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
	CONCURRENCY_MAX,
	CONCURRENCY_MIN,
	REQUEST_TIMEOUT_MAX_MS,
	REQUEST_TIMEOUT_MIN_MS,
	type StoreConfig,
	SWEEP_INTERVAL_MIN_MS,
} from "../../shared/storeConfig";
import { TUNABLES } from "../../shared/tunables";
import type { StoreDto, WsMessage } from "../../shared/types";
import { fmtDuration, fmtInterval } from "../lib/format";
import { fmtIntervalExact, parseInterval } from "../lib/parseInterval";
import { useTunables } from "../lib/tunables";
import { Button, stateBorderClass, StateLabel, stateTextClass } from "../ui";
import { useWs } from "../ws";
import { CategoriesPanel, type CategoriesPanelHandle } from "./CategoriesPanel";
import { Modal } from "./Modal";

type TabId = "general" | "categories" | "advanced";

const DEFAULT_CONCURRENCY = 1;
const DEFAULT_TIMEOUT_MS = 15_000;

// Starts at the floor rather than at five minutes: the fast end is what gets
// adjusted, and reaching it used to mean opening a unit dropdown.
const SWEEP_PRESETS: { label: string; ms: number }[] = [
	{ label: "30s", ms: 30_000 },
	{ label: "1m", ms: 60_000 },
	{ label: "5m", ms: 5 * 60_000 },
	{ label: "10m", ms: 10 * 60_000 },
	{ label: "30m", ms: 30 * 60_000 },
	{ label: "1h", ms: 60 * 60_000 },
	{ label: "6h", ms: 6 * 60 * 60_000 },
];

type SweepUiState =
	| { phase: "idle" }
	| { phase: "running"; pages: number; items: number; startedAt: number }
	| { phase: "done"; ingested: number; durationMs: number; problem: string | null }
	| { phase: "failed"; error: string };

async function patchStore(
	id: string,
	patch: { enabled?: boolean; config?: Record<string, unknown> },
): Promise<{ ok: boolean; error?: string }> {
	const res = await fetch(`/api/stores/${encodeURIComponent(id)}`, {
		method: "PATCH",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(patch),
	});
	const data = (await res.json().catch(() => ({}))) as {
		ok?: boolean;
		error?: string;
	};
	return { ok: !!data.ok && res.ok, error: data.error };
}

type Props = {
	store: StoreDto | null;
	open: boolean;
	onClose: () => void;
	onSaved: () => void;
};

export function StoreSettingsModal({ store, open, onClose, onSaved }: Props) {
	const [tab, setTab] = useState<TabId>("general");
	const [draft, setDraft] = useState<StoreConfig>({});
	const [enabled, setEnabled] = useState<boolean>(false);
	const [original, setOriginal] = useState<{
		config: StoreConfig;
		enabled: boolean;
	}>({ config: {}, enabled: false });
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [savedFlash, setSavedFlash] = useState(false);
	const [sweepUi, setSweepUi] = useState<SweepUiState>({ phase: "idle" });
	const [categoriesDirty, setCategoriesDirty] = useState(false);
	const categoriesRef = useRef<CategoriesPanelHandle>(null);

	useEffect(() => {
		if (!open || !store) return;
		setTab("general");
		setDraft({ ...(store.config ?? {}) });
		setEnabled(store.enabled);
		setOriginal({
			config: { ...(store.config ?? {}) },
			enabled: store.enabled,
		});
		setSaveError(null);
		setSavedFlash(false);
		setSweepUi({ phase: "idle" });
		setCategoriesDirty(false);
	}, [open, store?.id]);

	useWs((msg: WsMessage) => {
		if (!store) return;
		if (!("storeId" in msg) || msg.storeId !== store.id) return;
		if (msg.type === "sweep.started") {
			setSweepUi({
				phase: "running",
				pages: 0,
				items: 0,
				startedAt: msg.startedAt,
			});
		} else if (msg.type === "sweep.progress") {
			setSweepUi((prev) => (prev.phase === "running" ? { ...prev, pages: msg.pages, items: msg.items } : prev));
		} else if (msg.type === "sweep.complete") {
			setSweepUi({
				phase: "done",
				ingested: msg.ingested,
				durationMs: msg.durationMs,
				problem: msg.problem,
			});
		} else if (msg.type === "sweep.failed") {
			setSweepUi({ phase: "failed", error: msg.error });
		}
	});

	const configDirty = useMemo(() => {
		if (enabled !== original.enabled) return true;
		return JSON.stringify(draft) !== JSON.stringify(original.config);
	}, [draft, enabled, original]);

	const dirty = configDirty || categoriesDirty;

	const onSweepNow = async () => {
		if (!store) return;
		setSweepUi({ phase: "running", pages: 0, items: 0, startedAt: Date.now() });
		try {
			const res = await fetch(`/api/stores/${encodeURIComponent(store.id)}/sweep`, { method: "POST" });
			const data = (await res.json()) as { ok?: boolean; error?: string };
			if (!data.ok) {
				setSweepUi({ phase: "failed", error: data.error ?? "unknown" });
			}
		} catch (e) {
			setSweepUi({ phase: "failed", error: (e as Error).message });
		}
	};

	const onSave = async () => {
		if (!store) return;
		setSaving(true);
		setSaveError(null);
		try {
			if (configDirty) {
				const result = await patchStore(store.id, {
					enabled: enabled !== original.enabled ? enabled : undefined,
					config: draft as Record<string, unknown>,
				});
				if (!result.ok) {
					setSaveError(result.error ?? "Save failed");
					setSaving(false);
					return;
				}
			}
			if (categoriesDirty && categoriesRef.current) {
				await categoriesRef.current.save();
				setCategoriesDirty(false);
			}
			setOriginal({ config: { ...draft }, enabled });
			setSavedFlash(true);
			setTimeout(() => setSavedFlash(false), 1500);
			onSaved();
		} catch (e) {
			setSaveError((e as Error).message);
		} finally {
			setSaving(false);
		}
	};

	const onCancel = () => {
		if (dirty) {
			const ok = confirm("Discard unsaved changes?");
			if (!ok) return;
		}
		onClose();
	};

	if (!store) return null;

	const tabs: { id: TabId; label: string; icon: typeof SettingsIcon }[] = [
		{ id: "general", label: "General", icon: SettingsIcon },
		{ id: "categories", label: "Categories", icon: FolderTree },
		{ id: "advanced", label: "Advanced", icon: Gauge },
	];

	const stack = String(draft.stack ?? store.config?.stack ?? "—");

	return (
		<Modal
			open={open}
			onClose={onCancel}
			title={
				<>
					<span>{store.name}</span>
					<span className="rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted">{stack}</span>
				</>
			}
			subtitle={store.baseUrl}
			footer={
				<>
					{saveError && (
						<span className="mr-auto flex min-w-0 items-baseline gap-1.5">
							<StateLabel state="fault" label="Save failed" />
							<span className="min-w-0 text-xs break-words text-muted">{saveError}</span>
						</span>
					)}
					{savedFlash && (
						<span className="mr-auto flex items-center gap-1 text-xs text-ink">
							<CheckCircle2 className="size-3.5" aria-hidden />
							Saved
						</span>
					)}
					<Button variant="secondary" size="sm" onClick={onCancel}>
						Cancel
					</Button>
					<Button variant="primary" size="sm" onClick={onSave} disabled={!dirty || saving}>
						{saving ? (
							<Loader2 className="size-3.5 animate-spin" aria-hidden />
						) : (
							<Save className="size-3.5" aria-hidden />
						)}
						Save changes
					</Button>
				</>
			}
		>
			<div className="flex min-h-0 flex-1 flex-col sm:flex-row">
				<nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-hairline px-3 py-1.5 sm:w-40 sm:flex-col sm:gap-0.5 sm:border-r sm:border-b-0 sm:px-2 sm:py-2">
					{tabs.map((t) => {
						const Icon = t.icon;
						const active = tab === t.id;
						return (
							<button
								key={t.id}
								onClick={() => setTab(t.id)}
								className={`flex min-h-tap shrink-0 items-center gap-2 rounded-md px-2.5 text-left text-[13px] transition-colors ${
									active ? "bg-raised font-semibold text-ink" : "text-muted hover:bg-raised hover:text-ink"
								}`}
							>
								<Icon className="size-3.5 shrink-0" aria-hidden />
								<span>{t.label}</span>
							</button>
						);
					})}
				</nav>

				<div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-3">
					{tab === "general" && (
						<GeneralTab
							store={store}
							draft={draft}
							setDraft={setDraft}
							enabled={enabled}
							setEnabled={setEnabled}
							sweepUi={sweepUi}
							onSweepNow={onSweepNow}
						/>
					)}
					{tab === "categories" && (
						<CategoriesPanel ref={categoriesRef} storeId={store.id} onDirtyChange={setCategoriesDirty} />
					)}
					{tab === "advanced" && <AdvancedTab draft={draft} setDraft={setDraft} />}
				</div>
			</div>
		</Modal>
	);
}

function FieldLabel({ label, hint }: { label: string; hint?: string }) {
	return (
		<div className="mb-1.5 flex items-baseline justify-between gap-2">
			<span className="text-[13px] font-semibold text-ink">{label}</span>
			{hint && <span className="text-[11px] text-muted">{hint}</span>}
		</div>
	);
}

function Section({ children, className = "" }: { children: React.ReactNode; className?: string }) {
	return <section className={`rounded-[10px] border border-hairline bg-paper p-3 ${className}`}>{children}</section>;
}

function GeneralTab({
	store,
	draft,
	setDraft,
	enabled,
	setEnabled,
	sweepUi,
	onSweepNow,
}: {
	store: StoreDto;
	draft: StoreConfig;
	setDraft: React.Dispatch<React.SetStateAction<StoreConfig>>;
	enabled: boolean;
	setEnabled: (b: boolean) => void;
	sweepUi: SweepUiState;
	onSweepNow: () => void;
}) {
	const needsTosAck = store.id === "cardcenter" && !store.config?.tosAcked;

	const handleEnabledToggle = () => {
		if (!enabled && needsTosAck) {
			const ok = confirm(
				"Cardcenter's robots.txt explicitly forbids automated scraping and 'buy-for-me' agents. Enabling means you are knowingly going against their stated policy.\n\nContinue?",
			);
			if (!ok) return;
			setDraft((d) => ({
				...d,
				tosAcked: true,
				tosAckedAt: Date.now(),
			}));
			setEnabled(true);
			return;
		}
		setEnabled(!enabled);
	};

	const { get } = useTunables();
	const defaultMs = get(TUNABLES.sweepEvery) * 60_000;
	const currentMs = Number(draft.sweepIntervalMs ?? defaultMs);
	const matchedPreset = SWEEP_PRESETS.find((p) => p.ms === currentMs);
	const isCustom = !matchedPreset;

	// Seeded from the saved value so the field opens showing what is in effect
	// rather than an unrelated default.
	const [customText, setCustomText] = useState<string>(() => (isCustom ? fmtIntervalExact(currentMs) : ""));

	const setInterval = (ms: number) => {
		setDraft((d) => ({ ...d, sweepIntervalMs: ms }));
	};

	const parsed = customText.trim() === "" ? null : parseInterval(customText, SWEEP_INTERVAL_MIN_MS);
	const parseError = parsed && "error" in parsed ? parsed.error : null;
	// Narrowed separately: the compiler cannot carry `parsed`'s discriminant
	// through the `parseError` check below.
	const parsedMs = parsed && "ms" in parsed ? parsed.ms : null;

	const applyCustom = (text: string) => {
		setCustomText(text);
		const r = parseInterval(text, SWEEP_INTERVAL_MIN_MS);
		// Only a readable value moves the draft; an unreadable one leaves the
		// saved cadence alone and is reported under the field instead of being
		// swallowed, which is what used to happen.
		if ("ms" in r) setInterval(r.ms);
	};

	const pickPreset = (ms: number) => {
		setCustomText("");
		setInterval(ms);
	};

	const tooLow = currentMs < SWEEP_INTERVAL_MIN_MS;

	return (
		// Two columns where the modal is wide enough: the short sections pair
		// up, the long ones span. Order stays top to bottom, because the manual
		// sweep's hint points at the status "above".
		<div className="grid gap-3 sm:grid-cols-2">
			<Section className="sm:col-span-2">
				<FieldLabel label="Status" />
				<div className="flex items-center gap-2.5">
					<button
						onClick={handleEnabledToggle}
						className={`min-h-tap shrink-0 rounded-md border px-2.5 text-xs font-medium transition-colors ${
							enabled
								? "border-line bg-raised text-ink hover:bg-ground"
								: "border-hairline text-muted hover:border-line hover:text-ink"
						}`}
					>
						{enabled ? "Enabled" : "Disabled"}
					</button>
					<span className="text-xs text-muted">
						{enabled ? "Swept on the cadence below." : "Not swept. Products you watch at this store are still checked."}
					</span>
				</div>
			</Section>

			<Section className="sm:col-span-2">
				<FieldLabel label="Sweep cadence" hint={`min ${fmtInterval(SWEEP_INTERVAL_MIN_MS)}`} />
				<div className="flex flex-wrap items-center gap-1.5">
					{SWEEP_PRESETS.map((p) => {
						const active = matchedPreset?.ms === p.ms;
						return (
							<button
								key={p.label}
								onClick={() => pickPreset(p.ms)}
								className={`min-h-tap min-w-10 rounded-md border px-2.5 text-xs transition-colors ${
									active
										? "border-line bg-raised font-semibold text-ink"
										: "border-hairline text-muted hover:border-line hover:text-ink"
								}`}
							>
								{p.label}
							</button>
						);
					})}
				</div>

				<div className="mt-2 flex flex-wrap items-baseline gap-2">
					<input
						type="text"
						inputMode="text"
						value={customText}
						onChange={(e) => applyCustom(e.target.value)}
						placeholder="or type 50s, 2m, 1m30s…"
						aria-label="Custom sweep cadence"
						aria-invalid={parseError != null}
						className={`min-h-tap w-48 rounded-md border bg-ground px-2.5 text-[13px] text-ink placeholder:text-muted focus:outline-none ${
							parseError ? stateBorderClass("fault") : "border-hairline focus:border-line"
						}`}
					/>
					{parseError ? (
						<span className={`text-xs ${stateTextClass("fault")}`}>{parseError}</span>
					) : parsedMs !== null ? (
						<span className="text-xs text-ink">→ every {fmtIntervalExact(parsedMs)}</span>
					) : (
						<span className="text-xs text-muted">Seconds if you leave the unit off.</span>
					)}
				</div>

				<p className="mt-2 text-[11px] text-muted">
					Currently every <span className="font-medium text-ink">{fmtIntervalExact(currentMs)}</span>.
					{draft.sweepIntervalMs == null && <> Using the default from Settings.</>} A new cadence takes effect after the
					next sweep. Faster finds changes sooner, but every sweep reads the whole selection again.
				</p>
				{tooLow && (
					<p className="mt-1">
						<StateLabel state="fault" label={`Cadence must be at least ${fmtInterval(SWEEP_INTERVAL_MIN_MS)}`} />
					</p>
				)}
			</Section>

			<Section>
				<FieldLabel label="Manual sweep" />
				<div className="flex items-center gap-2">
					<Button variant="secondary" size="sm" onClick={onSweepNow} disabled={!enabled || sweepUi.phase === "running"}>
						{sweepUi.phase === "running" ? (
							<Loader2 className="size-3.5 animate-spin" aria-hidden />
						) : (
							<Play className="size-3.5" aria-hidden />
						)}
						Sweep now
					</Button>
					{!enabled && <span className="text-[11px] text-muted">Enable the store above first.</span>}
				</div>
				<SweepStatusLine ui={sweepUi} />
			</Section>

			<Section>
				<FieldLabel label="Display name" />
				<input
					type="text"
					value={store.name}
					readOnly
					className="min-h-tap w-full rounded-md border border-hairline bg-ground px-2.5 text-[13px] text-ink"
				/>
				<p className="mt-1 text-[11px] text-muted">Renaming stores isn't supported yet.</p>
			</Section>

			<Section className="sm:col-span-2">
				<FieldLabel label="Note" hint="Shown in the stores list as a warning" />
				<textarea
					value={String(draft.note ?? "")}
					onChange={(e) =>
						setDraft((d) => ({
							...d,
							note: e.target.value === "" ? undefined : e.target.value,
						}))
					}
					rows={3}
					placeholder="e.g. cardcenter forbids automated scraping; check ToS before enabling."
					className="w-full resize-y rounded-md border border-hairline bg-ground px-2.5 py-1.5 text-[13px] text-ink placeholder:text-muted focus:border-line focus:outline-none"
				/>
			</Section>

			{store.id === "cardcenter" && (
				<Section className="sm:col-span-2">
					<FieldLabel label="Terms-of-service acknowledgement" />
					{draft.tosAcked && draft.tosAckedAt ? (
						<p className="text-xs text-ink">Acknowledged on {new Date(draft.tosAckedAt).toLocaleString()}.</p>
					) : (
						<p className="text-xs text-muted">Not acknowledged. You'll be prompted when enabling.</p>
					)}
				</Section>
			)}
		</div>
	);
}

function SweepStatusLine({ ui }: { ui: SweepUiState }) {
	if (ui.phase === "idle") return null;
	if (ui.phase === "running") {
		return (
			<div className="mt-2 flex items-center gap-2 rounded-md border border-hairline bg-ground px-2.5 py-1.5 text-xs text-ink">
				<Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
				<span>
					sweeping… {ui.pages} {ui.pages === 1 ? "page" : "pages"} · {ui.items} {ui.items === 1 ? "item" : "items"}
				</span>
			</div>
		);
	}
	if (ui.phase === "done" && ui.problem) {
		return (
			<div className="mt-2 flex items-start gap-2 rounded-md border border-hairline bg-ground px-2.5 py-1.5 text-xs">
				<AlertTriangle className={`mt-0.5 size-3.5 shrink-0 ${stateTextClass("needs")}`} aria-hidden />
				<div className="flex min-w-0 flex-col gap-0.5">
					<StateLabel
						state="needs"
						label={`Done · ${ui.ingested} ${ui.ingested === 1 ? "item" : "items"} · came up short`}
					/>
					<span className="break-words text-ink">{ui.problem}</span>
				</div>
			</div>
		);
	}
	if (ui.phase === "done") {
		return (
			<div className="mt-2 flex items-center gap-2 rounded-md border border-hairline bg-ground px-2.5 py-1.5 text-xs text-ink">
				<CheckCircle2 className="size-3.5 shrink-0" aria-hidden />
				<span>
					Done · {ui.ingested} {ui.ingested === 1 ? "item" : "items"} · {fmtDuration(ui.durationMs)}
				</span>
			</div>
		);
	}
	return (
		<div className="mt-2 flex items-start gap-2 rounded-md border border-hairline bg-ground px-2.5 py-1.5 text-xs">
			<XCircle className="mt-0.5 size-3.5 shrink-0 text-muted" aria-hidden />
			<div className="flex min-w-0 flex-col gap-0.5">
				<StateLabel state="fault" label="Sweep failed" />
				<span className="break-words text-muted">{ui.error}</span>
			</div>
		</div>
	);
}

function AdvancedTab({
	draft,
	setDraft,
}: {
	draft: StoreConfig;
	setDraft: React.Dispatch<React.SetStateAction<StoreConfig>>;
}) {
	const concurrency = Number(draft.concurrency ?? DEFAULT_CONCURRENCY);
	const timeoutMs = Number(draft.requestTimeoutMs ?? DEFAULT_TIMEOUT_MS);
	const timeoutSec = Math.round(timeoutMs / 1000);

	return (
		<div className="grid gap-3 sm:grid-cols-2">
			<Section>
				<FieldLabel label="Concurrent requests" hint={`${CONCURRENCY_MIN}–${CONCURRENCY_MAX}`} />
				<div className="flex items-center gap-3">
					<input
						type="range"
						min={CONCURRENCY_MIN}
						max={CONCURRENCY_MAX}
						step={1}
						value={concurrency}
						onChange={(e) =>
							setDraft((d) => ({
								...d,
								concurrency: Number(e.target.value) | 0,
							}))
						}
						className="flex-1 accent-ink"
					/>
					<span className="w-8 text-right text-[15px] font-semibold font-tag text-ink">{concurrency}</span>
				</div>
				<p className="mt-1 text-[11px] text-muted">
					How many checks of watched products may run against this store at once. Sweeps always read one page at a time.
					Higher is faster but risks the store blocking us.
				</p>
			</Section>

			<Section>
				<FieldLabel
					label="Request timeout"
					hint={`${REQUEST_TIMEOUT_MIN_MS / 1000}–${REQUEST_TIMEOUT_MAX_MS / 1000}s`}
				/>
				<div className="flex items-center gap-2">
					<input
						type="number"
						min={REQUEST_TIMEOUT_MIN_MS / 1000}
						max={REQUEST_TIMEOUT_MAX_MS / 1000}
						step={1}
						value={timeoutSec}
						onChange={(e) => {
							const sec = Number(e.target.value);
							if (!Number.isFinite(sec) || sec <= 0) return;
							setDraft((d) => ({
								...d,
								requestTimeoutMs: Math.max(REQUEST_TIMEOUT_MIN_MS, Math.min(REQUEST_TIMEOUT_MAX_MS, sec * 1000)),
							}));
						}}
						className="min-h-tap w-20 rounded-md border border-hairline bg-ground px-2.5 text-[13px] text-ink focus:border-line focus:outline-none"
					/>
					<span className="text-xs text-muted">seconds</span>
				</div>
				<p className="mt-1 text-[11px] text-muted">
					How long to wait for the store to answer one request before giving up on it.
				</p>
			</Section>

			<Section className="sm:col-span-2">
				<FieldLabel label="Per-page throttle" />
				<div className="flex items-center gap-2">
					<span className="text-[13px] font-medium text-muted">500ms</span>
					<span className="rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted">Global</span>
				</div>
				<p className="mt-1 flex items-start gap-1.5 text-[11px] text-muted">
					<Info className="mt-0.5 size-3 shrink-0" aria-hidden />
					Every sweep pauses half a second between pages, for all stores. It can't be changed per store yet.
				</p>
			</Section>

			{/* Stays hand-rolled, not <Button variant="ghost">: ghost still
          carries Button's shared padded rounded-md box from its size
          classes, which would turn this plain inline text link into a
          padded button — the opposite of the minimal-emphasis look
          intended here. */}
			<button
				onClick={() =>
					setDraft((d) => ({
						...d,
						concurrency: undefined,
						requestTimeoutMs: undefined,
					}))
				}
				className="inline-flex min-h-tap items-center gap-1 justify-self-start text-xs text-muted hover:text-ink sm:col-span-2"
			>
				<RotateCcw className="size-3" aria-hidden />
				Reset to defaults
			</button>
		</div>
	);
}
